import { useState } from "react";
import { useAuth, API } from "@/App";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Search,
  Users,
  Phone,
  Mail,
  Globe,
  Building,
  MapPin,
  MessageCircle,
  Loader2,
  ArrowLeft
} from "lucide-react";
import { Link } from "react-router-dom";

const SearchPage = () => {
  const { token } = useAuth();
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const standardizePhoneForWhatsApp = (phone) => {
    if (!phone) return "";
    let clean = phone.replace(/[\s\-\.\(\)]/g, '');
    if (clean.startsWith('+39')) {
      return clean;
    } else if (clean.startsWith('39') && clean.length >= 11) {
      return '+' + clean;
    } else if (clean.startsWith('3') && clean.length === 10) {
      return '+39' + clean;
    } else if (clean.startsWith('0') && clean.length >= 9) {
      return '+39' + clean.substring(1);
    }
    return clean;
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    if (!query.trim() || query.trim().length < 2) {
      toast.warning("Inserisci almeno 2 caratteri per la ricerca");
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const response = await axios.post(
        `${API}/members/search`,
        { query: query.trim(), limit: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResults(response.data.members || []);
      
      if (response.data.count === 0) {
        toast.info("Nessun risultato trovato");
      } else {
        toast.success(`Trovati ${response.data.count} risultati`);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Errore nella ricerca");
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const isValidWebsite = (website) => {
    if (!website) return false;
    return website.includes('.') && !website.includes('successnet.it');
  };

  const getWhatsAppLink = (phone) => {
    const formatted = standardizePhoneForWhatsApp(phone);
    if (!formatted) return null;
    const cleanNumber = formatted.replace(/\+/g, '');
    return `https://wa.me/${cleanNumber}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-800">Ricerca Globale</h1>
              <p className="text-muted-foreground">Cerca membri in tutti i capitoli</p>
            </div>
          </div>
        </motion.div>

        {/* Search Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="mb-8">
            <CardContent className="pt-6">
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Cerca per nome, azienda, specializzazione, email o città..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-10 h-12 text-lg"
                    data-testid="global-search-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isSearching || query.trim().length < 2}
                  className="h-12 px-8"
                  data-testid="global-search-button"
                >
                  {isSearching ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Ricerca...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-5 w-5" />
                      Cerca
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Results */}
        {hasSearched && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {results.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Nessun risultato</h3>
                  <p className="text-muted-foreground">
                    Nessun membro trovato per "{query}"
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    {results.length} risultati trovati
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <TooltipProvider delayDuration={200}>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="font-semibold">Nome</TableHead>
                            <TableHead className="font-semibold">Azienda</TableHead>
                            <TableHead className="font-semibold">Specializzazione</TableHead>
                            <TableHead className="font-semibold">Capitolo</TableHead>
                            <TableHead className="font-semibold">Città</TableHead>
                            <TableHead className="font-semibold text-center w-32">Contatti</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {results.map((member, index) => {
                            const phoneFormatted = standardizePhoneForWhatsApp(member.phone);
                            const whatsappLink = getWhatsAppLink(member.phone);
                            
                            return (
                              <TableRow key={index} className="hover:bg-muted/50">
                                <TableCell className="font-medium">{member.name}</TableCell>
                                <TableCell className="text-muted-foreground max-w-[180px] truncate">
                                  {member.company || "-"}
                                </TableCell>
                                <TableCell className="text-muted-foreground max-w-[200px] truncate">
                                  {member.specialization || "-"}
                                </TableCell>
                                <TableCell>
                                  <span className="text-sm font-medium text-blue-600">
                                    {member.chapter_name || "Sconosciuto"}
                                  </span>
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {member.city || "-"}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center justify-center gap-1">
                                    {/* Phone */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className={`h-8 w-8 rounded flex items-center justify-center ${phoneFormatted ? 'bg-blue-100 cursor-pointer hover:bg-blue-200' : 'bg-gray-100 opacity-40'}`}>
                                          {phoneFormatted ? (
                                            <a href={`tel:${member.phone}`}>
                                              <Phone className="h-4 w-4 text-blue-600" />
                                            </a>
                                          ) : (
                                            <Phone className="h-4 w-4 text-gray-400" />
                                          )}
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{phoneFormatted || "Telefono non disponibile"}</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Email */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className={`h-8 w-8 rounded flex items-center justify-center ${member.email ? 'bg-purple-100 cursor-pointer hover:bg-purple-200' : 'bg-gray-100 opacity-40'}`}>
                                          {member.email ? (
                                            <a href={`mailto:${member.email}`}>
                                              <Mail className="h-4 w-4 text-purple-600" />
                                            </a>
                                          ) : (
                                            <Mail className="h-4 w-4 text-gray-400" />
                                          )}
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{member.email || "Email non disponibile"}</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* Website */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className={`h-8 w-8 rounded flex items-center justify-center ${isValidWebsite(member.website) ? 'bg-green-100 cursor-pointer hover:bg-green-200' : 'bg-gray-100 opacity-40'}`}>
                                          {isValidWebsite(member.website) ? (
                                            <a href={member.website} target="_blank" rel="noopener noreferrer">
                                              <Globe className="h-4 w-4 text-green-600" />
                                            </a>
                                          ) : (
                                            <Globe className="h-4 w-4 text-gray-400" />
                                          )}
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{isValidWebsite(member.website) ? member.website : "Sito non disponibile"}</p>
                                      </TooltipContent>
                                    </Tooltip>

                                    {/* WhatsApp */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className={`h-8 w-8 rounded flex items-center justify-center ${whatsappLink ? 'bg-emerald-100 cursor-pointer hover:bg-emerald-200' : 'bg-gray-100 opacity-40'}`}>
                                          {whatsappLink ? (
                                            <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                                              <MessageCircle className="h-4 w-4 text-emerald-600" />
                                            </a>
                                          ) : (
                                            <MessageCircle className="h-4 w-4 text-gray-400" />
                                          )}
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{whatsappLink ? "Apri WhatsApp" : "WhatsApp non disponibile"}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </TooltipProvider>
                </CardContent>
              </Card>
            )}
          </motion.div>
        )}

        {/* Initial State */}
        {!hasSearched && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <CardContent className="py-16 text-center">
                <Search className="h-20 w-20 text-muted-foreground/50 mx-auto mb-6" />
                <h3 className="text-2xl font-semibold mb-3 text-slate-700">Cerca membri BNI</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Inserisci un termine di ricerca per trovare membri in tutti i capitoli salvati.
                  Puoi cercare per nome, azienda, specializzazione, email o città.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
